import { CONCEPTS, type Lesson, type LessonResult } from '@baduk/coach';
import { colorName } from '@baduk/engine';

export interface LessonPanelProps {
  readonly lessons: readonly Lesson[];
  readonly active: Lesson | null;
  readonly result: LessonResult | null;
  readonly solved: ReadonlySet<string>;
  readonly hintShown: boolean;
  onStart(lesson: Lesson): void;
  onRetry(): void;
  onHint(): void;
  onLeave(): void;
  readonly open: boolean;
  onOpenChange(open: boolean): void;
}

/**
 * Lessons: a fixed position, one thing to do, and an honest verdict.
 *
 * The rest of the app teaches opportunistically — it explains what happens to
 * turn up in a game. This is the other half: a position chosen so that one idea
 * is the only thing standing between the learner and the answer.
 *
 * A failed attempt says what went wrong rather than only that it did, because
 * "not two eyes yet" and "that group is dead now" are different mistakes and
 * the difference is the lesson.
 */
export function LessonPanel({
  lessons,
  active,
  result,
  solved,
  hintShown,
  onStart,
  onRetry,
  onHint,
  onLeave,
  open,
  onOpenChange,
}: LessonPanelProps) {
  if (active) {
    const concept = CONCEPTS[active.concept];
    const done = result?.status === 'solved';
    const failed = result?.status === 'failed';
    const index = lessons.findIndex((l) => l.id === active.id);
    const next = lessons[index + 1];

    return (
      <section className="lesson">
        <div className="lesson-head">
          <span className="muted small">
            Lesson {index + 1} of {lessons.length} · {colorName(active.toPlay)} to play
          </span>
          <h2>{active.title}</h2>
        </div>

        <p>{active.brief}</p>

        {done && (
          <div className="lesson-verdict solved">
            <strong>{result.message}</strong>
            <p>{active.reward}</p>
          </div>
        )}

        {failed && (
          <div className="lesson-verdict failed">
            <strong>Not this time</strong>
            <p>{result.message}</p>
          </div>
        )}

        {!done && hintShown && <p className="lesson-hint">{active.hint}</p>}

        <div className="lesson-actions">
          {done && next ? (
            <button type="button" className="primary" onClick={() => onStart(next)}>
              Next lesson
            </button>
          ) : null}
          {!done && !hintShown && (
            <button type="button" onClick={onHint}>
              Hint
            </button>
          )}
          {(failed || done) && (
            <button type="button" onClick={onRetry}>
              {done ? 'Play it again' : 'Try again'}
            </button>
          )}
          <button type="button" onClick={onLeave}>
            {done && !next ? 'Back to playing' : 'Leave'}
          </button>
        </div>

        {concept && (
          <details className="concept">
            <summary>{concept.title}</summary>
            <p>{concept.whyItMatters}</p>
          </details>
        )}
      </section>
    );
  }

  return (
    <details
      className="lesson-list"
      open={open}
      onToggle={(event) => onOpenChange((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        <span className="lesson-title">Lessons</span>
        <span className="muted small">
          {solved.size} of {lessons.length} done
        </span>
      </summary>

      <p className="muted small">
        One position, one idea, one move. The board tells you whether it worked and why.
      </p>

      <ul>
        {lessons.map((lesson) => (
          <li key={lesson.id}>
            <button type="button" className="lesson-row" onClick={() => onStart(lesson)}>
              <span className="lesson-tick" aria-hidden="true">
                {solved.has(lesson.id) ? '✓' : '·'}
              </span>
              <span>
                <strong>{lesson.title}</strong>
                <span className="muted small">{CONCEPTS[lesson.concept]?.title}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}
